// Sales Rep report — per-rep rollup of presentations / estimates /
// sales orders / invoiced dollars in the range. Click a rep to list their
// invoices, orders, estimates or presentations below (Stephanie 2026-10-16).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ReportRowsTable, SortHeader, type ReportList } from "@/components/ReportRowsTable";
import { nextSort, sortRows, type SortState } from "@/lib/report-rows";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import {
  ReportShell,
  useReportRange,
} from "@/components/ReportShell";

interface SalesRepRow {
  repId: string;
  repName: string;
  email: string;
  presentations: number;
  estimates: number;
  salesOrders: number;
  invoiced: { count: number; total: string };
}

interface Report {
  range: { from: string; to: string };
  rows: SalesRepRow[];
  fetchedAt: string;
}

function formatCurrency(amount: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

type RepSortKey = "rep" | "presentations" | "estimates" | "salesOrders" | "invoiced";
const REP_SORT: Record<RepSortKey, (r: SalesRepRow) => string | number> = {
  rep: (r) => r.repName,
  presentations: (r) => r.presentations,
  estimates: (r) => r.estimates,
  salesOrders: (r) => r.salesOrders,
  invoiced: (r) => Number(r.invoiced.total),
};

const LISTS: Array<{ list: ReportList; label: string }> = [
  { list: "invoices", label: "Invoices" },
  { list: "sales_orders", label: "Sales orders" },
  { list: "estimates", label: "Estimates" },
  { list: "presentations", label: "Presentations" },
];

export function ReportSalesRepPage() {
  const { preset, setPreset, range, setCustom, repId, setRepId } = useReportRange();
  const [list, setList] = useState<ReportList>("invoices");
  const [sort, setSort] = useState<SortState<RepSortKey>>({ key: "invoiced", dir: "desc" });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-rep", range.from, range.to],
    queryFn: () =>
      api<Report>(`/reports/sales-rep?from=${range.from}&to=${range.to}`),
  });

  const rows = sortRows(data?.rows ?? [], REP_SORT[sort.key], sort.dir);
  const onSort = (k: RepSortKey) => setSort((cur) => nextSort(cur, k));

  return (
    <ReportShell
      title="Sales Rep"
      subtitle="Each rep's numbers for the range. Click a column to sort; click a rep to see their invoices, orders, estimates and presentations."
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      onCustomRange={setCustom}
      fetchedAt={data?.fetchedAt}
    >
      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
          Loading…
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">
          Couldn't load report.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
          No rep activity in this window.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
          <table className="w-full">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <SortHeader label="Rep" sortKey="rep" sort={sort} onSort={onSort} className="px-5 py-3" />
                <SortHeader label="Presentations" sortKey="presentations" sort={sort} onSort={onSort} align="right" className="px-5 py-3" />
                <SortHeader label="Estimates" sortKey="estimates" sort={sort} onSort={onSort} align="right" className="px-5 py-3" />
                <SortHeader label="Sales orders" sortKey="salesOrders" sort={sort} onSort={onSort} align="right" className="px-5 py-3" />
                <SortHeader label="Invoiced" sortKey="invoiced" sort={sort} onSort={onSort} align="right" className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {rows.map((r) => (
                <tr
                  key={r.repId}
                  className={cn("cursor-pointer hover:bg-cream-50", repId === r.repId && "bg-cream-100")}
                  onClick={() => setRepId(repId === r.repId ? "" : r.repId)}
                >
                  <td className="px-5 py-4">
                    <button type="button" aria-pressed={repId === r.repId} className="text-left font-semibold text-brown-500 hover:underline">
                      {r.repName}
                    </button>
                    <div className="text-xs text-brown-300">{r.email}</div>
                  </td>
                  <td className="px-5 py-4 text-right text-brown-500">
                    {r.presentations.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-right text-brown-500">
                    {r.estimates.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-right text-brown-500">
                    {r.salesOrders.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="font-semibold text-brown-500">
                      {formatCurrency(r.invoiced.total)}
                    </div>
                    <div className="text-xs text-brown-300">
                      {r.invoiced.count} invoice
                      {r.invoiced.count === 1 ? "" : "s"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          {repId ? `${rows.find((r) => r.repId === repId)?.repName ?? "This rep"}'s` : "Everyone's"}
        </span>
        {LISTS.map((l) => (
          <button
            key={l.list}
            type="button"
            onClick={() => setList(l.list)}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
              list === l.list ? "bg-brown-500 text-cream-50" : "bg-cream-100 text-brown-400 hover:bg-cream-200",
            )}
          >
            {l.label}
          </button>
        ))}
        {repId && (
          <button type="button" onClick={() => setRepId("")} className="ml-1 text-xs text-brown-300 underline">
            Show everyone
          </button>
        )}
      </div>
      <ReportRowsTable
        list={list}
        range={range}
        repId={repId || undefined}
        title={LISTS.find((l) => l.list === list)?.label ?? ""}
      />
    </ReportShell>
  );
}
